import { WithAuthorization } from "../../context";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Center, Flex, Heading, Text, useBreakpointValue, useToast } from "@chakra-ui/react";
import { YearSelector } from "../../components";
import { QuarterSelector } from "../../components/QuarterSelector";
import * as React from "react";
import { useEffect, useState } from "react";
import { QUARTERS, showErrorToast, translateSubElementString } from "../../utils";
import { useGetSupplierEmissionsPerYear, useGetSupplierEmissionsReport } from "../../hooks/query/reports";
import { ScopeEmissionsPerYearChart, ScopeTotalsAndApportioned } from "./components";
import {
  getScopeAssociatedEmissionsChartTooltip,
  mapScopeAssociatedEmissionsChartData,
  mapEmissionsPerYearData
} from "./helpers";
import { SupplierSpecificReportCharts } from "../SupplierDashboard/components/SupplierSpecificReport/components";
import LegendlessDonutChart from "../../components/LegendlessDonutChart/LegendlessDonutChart";
import { ReturnToList } from "../SupplierQuestionnaire/components/ReturnToList";
import { routes } from "../../config";
import { DecarbonizationChart } from "../Decarbonization/components";
import {
  useGetSupplierDecarbonization,
  useGetSupplierReducedDecarbonizationChartData,
  useInitialiseSupplierDecarbonization
} from "../../hooks/query";
import { CHART_COLORS, getRandomColor } from "../Decarbonization/helpers";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";

function SupplierEmissionsReport() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierEmissionsReport"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectorFlow = useBreakpointValue({
    md: "row",
    base: "column"
  });
  const containerFlow = useBreakpointValue({
    xl: "row",
    base: "column"
  });

  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get("id");
  const initialYear = searchParams.get("year");

  const [selectedYear, setSelectedYear] = useState(initialYear ?? (new Date().getFullYear() - 1).toString());
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTERS.ALL);
  const [decarbChartData, setDecarbChartData] = useState();
  const [decarbonisationData, setDecarbonisationData] = useState(null);
  const [isInitialised, setIsInitialised] = useState(false);

  const {
    data: supplierEmissionsReportData
  } = useGetSupplierEmissionsReport(supplierId, { year: selectedYear, quarter: selectedQuarter }, {
    enabled: supplierId !== null && typeof supplierId !== "undefined",
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const {
    data: supplierEmissionsPerYear
  } = useGetSupplierEmissionsPerYear(supplierId, {
    enabled: supplierId !== null && typeof supplierId !== "undefined",
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const renderRevenueTotals = (data) => {
    return <Flex gap={4} flexFlow="column" marginTop={5}>
      <ScopeTotalsAndApportioned
        data={data}
        organizationName={supplierEmissionsReportData?.organizationName ?? ""}
        scopeIndex={1}
      />
      <ScopeTotalsAndApportioned
        data={data}
        organizationName={supplierEmissionsReportData?.organizationName ?? ""}
        scopeIndex={2}
      />
      <ScopeTotalsAndApportioned
        data={data}
        organizationName={supplierEmissionsReportData?.organizationName ?? ""}
        scopeIndex={3}
      />
      <Text fontSize="sm">
        {`* ${t("labels.scopeThreeCalcRange")}`}
      </Text>
    </Flex>;
  };

  const {
    refetch: refetchChartData
  } = useGetSupplierReducedDecarbonizationChartData(supplierId, {
    enabled: decarbonisationData !== null && supplierId !== null && typeof supplierId !== "undefined",
    onSuccess: (data) => {
      let dataElements = [];
      data.data.forEach(scenario => {
        dataElements = dataElements.concat(scenario.data);
      });

      const maxValue = Math.max(...dataElements.map(el => el.y));
      const maxYear = Math.max(...dataElements.map(el => Number(el.x)));

      const coloredChartData = data.data.map(el => {
        return {
          ...el,
          color: CHART_COLORS[el.id] ?? getRandomColor()
        };
      });

      const newData = {
        baseYear: data.organization?.baseYear,
        companyName: data.organization?.name,
        baselineEmissionsTotal: data.baselineEmissionsTotal,
        chartData: coloredChartData,
        maxValue: maxValue + 0.05 * maxValue,
        maxYear: maxYear
      };
      setDecarbChartData(newData);
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const handleGetDecarbonisationSuccess = (data) => {
    setDecarbonisationData(data);
    refetchChartData();
  };

  const { mutate: initialise } = useInitialiseSupplierDecarbonization(supplierId, {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: (data) => handleGetDecarbonisationSuccess(data),
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    enabled: supplierId !== null && typeof supplierId !== "undefined"
  });

  useEffect(() => {
    if (decarbChartData && decarbChartData.baselineEmissionsTotal === 0 && !isInitialised) {
      setIsInitialised(true);
      initialise();
    }
  }, [decarbChartData, initialise, isInitialised]);

  const handleDecarbonisationError = (err) => {
    if (err.response?.status === 404) {
      setIsInitialised(true);
      return initialise();
    }
    showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
  };

  useGetSupplierDecarbonization(supplierId, {
    onSuccess: (data) => handleGetDecarbonisationSuccess(data),
    onError: err => handleDecarbonisationError(err),
    cacheTime: 0,
    enabled: supplierId !== null && typeof supplierId !== "undefined"
  });

  return (
    <Page title={t("page.title", { supplier: supplierEmissionsReportData?.supplier ?? "" })}>
      <Card>
        <Flex direction="column" gap={8}>
          <Flex
            gap={2}
            flexFlow={selectorFlow}
            alignItems={selectorFlow === "row" ? "center" : "start"}
            justifyContent={selectorFlow === "row" ? "space-between" : null}
          >
            <Flex gap={2} flexFlow={selectorFlow}>
              <YearSelector
                selectedYear={selectedYear}
                handleYearSelection={(value) => setSelectedYear(value)}
                groupStyles={{ marginBlock: 0 }}
              />
              <QuarterSelector
                selectedQuarter={selectedQuarter}
                handleQuarterSelection={(value) => setSelectedQuarter(value)}
                groupStyles={{ marginBlock: 0 }}
              />
            </Flex>
            <ReturnToList
              onClickHandler={() => navigate(routes.supplierPortalViewEmissions)}
              marginInlineStart={0}
            />
          </Flex>

          {supplierEmissionsReportData ? <Center>
            <Heading size="md">
              {t("page.title", { supplier: supplierEmissionsReportData?.supplier ?? "" })}
            </Heading>
          </Center> : null}

          {supplierEmissionsReportData && typeof supplierEmissionsReportData !== "undefined" &&
          supplierEmissionsReportData.revenue && typeof supplierEmissionsReportData.revenue !== "undefined" ?
            <Flex direction="column" gap={4}>
              <Heading size="md">
                {`${globalTranslation("scope")} 1/2/3 ${supplierEmissionsReportData?.organizationName ?? ""}`} {translateSubElementString("supplierEmissionsReport.labels.associatedEmissionsSection")}
              </Heading>

              <Flex height="100%" flexFlow={containerFlow} gap={containerFlow === "row" ? 0 : 4}>
                <Box width={containerFlow === "row" ? "50%" : "100%"} flexGrow="1">
                  {renderRevenueTotals(supplierEmissionsReportData.revenue)}
                </Box>
                <Box width={containerFlow === "row" ? "50%" : "100%"} flexGrow="1" height={"250px"}>
                  <LegendlessDonutChart
                    data={mapScopeAssociatedEmissionsChartData(supplierEmissionsReportData.revenue)}
                    getTooltip={getScopeAssociatedEmissionsChartTooltip}
                    getLabel={(el) => {
                      return el.data.value && el.data.value > 0 ?
                        el.data.value.toLocaleString("en-GB", { maximumFractionDigits: 4 }) : "";
                    }}
                    getLabelsTextColor={(el) => el.data?.id === 1 ? "black" : "white"}
                  />
                </Box>
              </Flex>
            </Flex> : null
          }

          {supplierEmissionsReportData && typeof supplierEmissionsReportData !== "undefined" ?
            <SupplierSpecificReportCharts reportData={supplierEmissionsReportData} showScopeTotals={false} /> :
            <Text fontSize="lg" fontWeight="bold">
              {globalTranslation("noDataAvailable")}
            </Text>
          }

          <Flex direction="column" gap={4}>
            <Heading size="md">
              {t("labels.scopeEmissionsPerYear")}
            </Heading>
            {supplierEmissionsPerYear && typeof supplierEmissionsPerYear !== "undefined" &&
            supplierEmissionsPerYear.filter(el => el.revenue && typeof el.revenue !== "undefined").length > 0 ?
              <ScopeEmissionsPerYearChart data={mapEmissionsPerYearData(supplierEmissionsPerYear)} /> :
              <Text fontSize="lg" fontWeight="bold">
                {globalTranslation("noDataAvailable")}
              </Text>}
          </Flex>

          <Flex direction="column" gap={3}>
            <Heading size="md">
              {t("labels.decarbonisation")}
            </Heading>
            <Flex gap={1} alignItems={"center"}>
              <Text fontSize="lg">
                {t("labels.estimatedYearlyEmissionsGrowth")}:
              </Text>
              <Text fontWeight="bold" fontSize="lg">
                {decarbonisationData !== null && typeof decarbonisationData !== "undefined" ?
                  decarbonisationData.estimatedYearlyEmissionsGrowth + "%" : "n/a"}
              </Text>
            </Flex>
            {decarbChartData && <DecarbonizationChart
              width={"100%"}
              marginBlockStart={0}
              data={decarbChartData}
              isDataAssociated={true}
              chartMargin={{ top: 10, right: 140, bottom: 50, left: 60 }}
              tickMultiplier={1}
              legends={[
                {
                  anchor: "top-right",
                  direction: "column",
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 0,
                  itemDirection: "left-to-right",
                  itemWidth: 100,
                  itemHeight: 34,
                  itemOpacity: 1,
                  symbolSize: 12,
                  symbolShape: "circle",
                  symbolBorderColor: "rgba(9, 9, 9, .5)",
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemBackground: "rgba(0, 0, 0, .03)",
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
            />}
          </Flex>
        </Flex>
      </Card>
    </Page>
  );
}

export default WithAuthorization(SupplierEmissionsReport);